import { Building2, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import useSWR from 'swr'

import { AdminAccountsTable } from '@/components/AccountsTable/AdminAccountsTable'
import { EmptyState } from '@/components/EmptyState'
import { PageHeader, SegmentedTabs, StatStrip } from '@/components/ds'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { daysSince } from '@/containers/AdminAccounts/adminFormat'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import { Collection } from '@/interfaces/Collection'
import { AdminAccount } from '@/types/Account'

type Filter = 'active' | 'abandoned' | 'all'
type Segment = 'recent' | 'noDocuments' | null

const Count = ({ value }: { value: number }) => (
  <span className="text-2xl font-medium tracking-[-0.02em] tabular">{value.toLocaleString('cs-CZ')}</span>
)

export default function AdminAccountsPage() {
  const [filter, setFilter] = useState<Filter>('active')
  const [segment, setSegment] = useState<Segment>(null)
  const [query, setQuery] = useState('')

  const { data: accounts, isLoading, mutate } = useSWR<Collection<AdminAccount>>('/api/v2/admin/accounts', apiFetcher)

  useSetPageHeader({
    breadcrumbs: [{ href: '/admin/accounts', children: 'Účty' }],
  })

  const all = useMemo(() => accounts?.data ?? [], [accounts])

  const stats = useMemo(() => {
    const live = all.filter((a) => !a.abandoned)
    return {
      total: live.length,
      recent: live.filter((a) => daysSince(a.lastDocumentCreatedAt) <= 30).length,
      noDocuments: live.filter((a) => !a.lastDocumentCreatedAt).length,
      abandoned: all.length - live.length,
    }
  }, [all])

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    return all
      .filter((a) => (filter === 'all' ? true : filter === 'abandoned' ? a.abandoned : !a.abandoned))
      .filter((a) => (segment === 'recent' ? daysSince(a.lastDocumentCreatedAt) <= 30 : segment === 'noDocuments' ? !a.lastDocumentCreatedAt : true))
      .filter((a) => !q || [a.name, a.id, a.ownerId?.email, a.ownerId?.fullName ?? ''].some((v) => v?.toLowerCase().includes(q)))
  }, [all, filter, segment, query])

  const toggleSegment = (value: Exclude<Segment, null>) => {
    setSegment((s) => (s === value ? null : value))
    if (filter === 'abandoned') setFilter('active')
  }

  return (
    <>
      <title>Účty</title>
      <div className="mx-auto w-full max-w-[1280px]">
        <PageHeader title="Účty" description={accounts ? `${all.length} registrovaných účtů` : 'Načítám'} />

        <StatStrip
          className="mb-6"
          items={[
            { label: 'Aktivní účty', value: <Count value={stats.total} />, hint: 'bez opuštěných', active: filter === 'active' && !segment, onClick: () => { setFilter('active'); setSegment(null) } },
            { label: 'Doklad za 30 dní', value: <Count value={stats.recent} />, hint: 'vystavili doklad', active: segment === 'recent', onClick: () => toggleSegment('recent') },
            { label: 'Bez dokladů', value: <Count value={stats.noDocuments} />, hint: 'zatím nic nevystavili', active: segment === 'noDocuments', onClick: () => toggleSegment('noDocuments') },
            { label: 'Opuštěné', value: <Count value={stats.abandoned} />, hint: 'označené ručně', active: filter === 'abandoned', onClick: () => { setFilter(filter === 'abandoned' ? 'active' : 'abandoned'); setSegment(null) } },
          ]}
        />

        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <SegmentedTabs<Filter>
            value={filter}
            onChange={(v) => { setFilter(v); setSegment(null) }}
            options={[
              { value: 'active', label: 'Aktivní', count: stats.total },
              { value: 'abandoned', label: 'Opuštěné', count: stats.abandoned },
              { value: 'all', label: 'Vše', count: all.length },
            ]}
          />
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Název, ID nebo e-mail vlastníka" className="pl-8" />
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border">
          {isLoading && (
            <div className="divide-y">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex h-14 items-center gap-6 px-3">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="ml-auto h-4 w-24" />
                </div>
              ))}
            </div>
          )}

          {!isLoading && visible.length > 0 && <AdminAccountsTable accounts={visible} refresh={() => mutate()} />}

          {!isLoading && visible.length === 0 && (
            <div className="p-6">
              <EmptyState icon={Building2}
                title={all.length ? 'Žádný účet neodpovídá filtru' : 'Zatím tu nejsou žádné účty'}
                description={all.length ? 'Zkuste jiný filtr nebo hledaný výraz.' : undefined}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}
